import {
  CONTACT_PROFILE_FIELDS,
  CONTACT_PROFILE_GROUPS,
  formatAmountInput,
  formatContactProfileValue,
  parseAmountInput,
  parsePercentInput,
  type ContactProfileField,
  type ContactProfileFieldKey,
  type ContactProfileGroupKey,
  type ContactProfileValues,
} from '@brimo/shared';

/**
 * Pure logic of the „Alle Eigenschaften\" dialog: the groups and rows the search leaves, the edit drafts
 * of a group and the PATCH body of its changed keys.
 */

export interface ProfileRow {
  field: ContactProfileField;
  /** The formatted value („4.980 €\", „ja\", „—\"). */
  display: string;
}

export type ProfileSection =
  | {
      kind: 'group';
      key: ContactProfileGroupKey;
      label: string;
      /** At least one field of the group may be edited by hand (all but „Herkunft & Terminierung\"). */
      editable: boolean;
      /** The role may read the group but not edit it (the financial groups for setters). */
      readOnly: boolean;
      rows: ProfileRow[];
    }
  /** The financial groups the role must not see, collapsed into one line. */
  | { kind: 'hidden' };

export function normalizeSearch(text: string): string {
  return text.normalize('NFKD').replace(/[̀-ͯ]/g, '').toLocaleLowerCase('de-DE').replace(/\s+/g, ' ').trim();
}

export interface ProfileSectionOptions {
  /** Search text: rows whose label or formatted value contain it; groups without hits disappear. */
  query?: string | undefined;
  /** A group shown completely whatever the search (the group being edited). */
  pinnedGroup?: ContactProfileGroupKey | null | undefined;
  /** Groups the role may read but not edit (`readOnlyGroups` of the profile response). */
  readOnlyGroups?: readonly ContactProfileGroupKey[] | undefined;
}

/** The sections of the dialog in catalog order; hidden groups appear once as `hidden` while nothing is searched. */
export function profileSections(
  values: ContactProfileValues,
  hiddenGroups: readonly ContactProfileGroupKey[],
  options: ProfileSectionOptions = {},
): ProfileSection[] {
  const needle = normalizeSearch(options.query ?? '');
  const sections: ProfileSection[] = [];
  let hiddenShown = false;
  for (const group of CONTACT_PROFILE_GROUPS) {
    if (hiddenGroups.includes(group.key)) {
      if (!needle && !hiddenShown) sections.push({ kind: 'hidden' });
      hiddenShown = true;
      continue;
    }
    const fields = CONTACT_PROFILE_FIELDS.filter((field) => field.group === group.key);
    const rows = fields.map((field) => ({
      field,
      display: formatContactProfileValue(field, values[field.key]),
    }));
    const showAll = !needle || options.pinnedGroup === group.key;
    const visible = showAll
      ? rows
      : rows.filter(
          (row) =>
            normalizeSearch(row.field.label).includes(needle) ||
            normalizeSearch(row.display).includes(needle),
        );
    if (visible.length === 0) continue;
    sections.push({
      kind: 'group',
      key: group.key,
      label: group.label,
      editable: fields.some((field) => field.editable),
      readOnly: options.readOnlyGroups?.includes(group.key) ?? false,
      rows: visible,
    });
  }
  return sections;
}

export type ProfileDraft = Partial<Record<ContactProfileFieldKey, string>>;

/**
 * The text an input of the field starts with: amounts as „4.980,00\", percentages in the number input's dot
 * notation („42.5\"), booleans as `true`/`false`, null as ''.
 */
export function draftText(field: ContactProfileField, value: unknown): string {
  if (value === null || value === undefined) return '';
  if (field.type === 'cents' && typeof value === 'number') return formatAmountInput(value);
  if (field.type === 'percent' && typeof value === 'number') return String(value);
  if (field.type === 'boolean') return value === true ? 'true' : value === false ? 'false' : '';
  return typeof value === 'string' || typeof value === 'number' ? String(value) : '';
}

export function draftOf(group: ContactProfileGroupKey, values: ContactProfileValues): ProfileDraft {
  const draft: ProfileDraft = {};
  for (const field of CONTACT_PROFILE_FIELDS)
    if (field.group === group && field.editable) draft[field.key] = draftText(field, values[field.key]);
  return draft;
}

/**
 * The raw value of a typed text. Empty means null (the field is cleared). An unreadable amount or number
 * is sent as typed so that the server's validation answers with its German field message.
 */
export function draftValue(field: ContactProfileField, text: string): string | number | boolean | null {
  const trimmed = text.trim();
  if (!trimmed) return null;
  switch (field.type) {
    case 'cents':
      return parseAmountInput(trimmed) ?? trimmed;
    case 'percent':
      return parsePercentInput(trimmed) ?? trimmed;
    case 'integer':
      return /^\d+$/.test(trimmed) ? Number(trimmed) : trimmed;
    case 'boolean':
      return trimmed === 'true' ? true : trimmed === 'false' ? false : null;
    case 'date':
    case 'enum':
    case 'text':
      return trimmed;
  }
}

function stored(value: unknown): string | number | boolean | null {
  if (value === undefined || value === null) return null;
  if (typeof value === 'string') return value.trim() || null;
  if (typeof value === 'number' || typeof value === 'boolean') return value;
  return null;
}

/** Only the keys of the group whose value differs from the stored one — the PATCH body. */
export function profileChanges(
  group: ContactProfileGroupKey,
  draft: ProfileDraft,
  values: ContactProfileValues,
): ContactProfileValues {
  const body: ContactProfileValues = {};
  for (const field of CONTACT_PROFILE_FIELDS) {
    if (field.group !== group || !field.editable) continue;
    const text = draft[field.key];
    if (text === undefined) continue;
    const next = draftValue(field, text);
    if (next !== stored(values[field.key])) body[field.key] = next;
  }
  return body;
}

const FIELD_BY_KEY = new Map<string, ContactProfileField>(
  CONTACT_PROFILE_FIELDS.map((field) => [field.key, field]),
);

/** „Nettoeinkommen mtl.: 3.900 € → 4.200 €\"; null for a key outside the catalog. */
export function changeLine(key: string, before: unknown, after: unknown): string | null {
  const field = FIELD_BY_KEY.get(key);
  if (!field) return null;
  return `${field.label}: ${formatContactProfileValue(field, before)} → ${formatContactProfileValue(field, after)}`;
}
